// Product Card Title Alignment Script
// Automatically aligns product card titles to the same height within each row
// Sets heights before scroll animations trigger for seamless visual experience
(function() {
  'use strict';

  // Configuration constants
  const BREAKPOINT = 901,    // Minimum viewport width for activation
        DEBOUNCE = 100,      // Debounce delay for resize events
        ITEMS_PER_ROW = 4;   // Number of items per row for alignment

  // State variables
  let resizeTimeout,
      isInit = false;

  // Get all product title elements from the grid
  function getTitles() {
    const grid = document.querySelector('#product-grid');
    return grid ? Array.from(grid.querySelectorAll('.card > .card__content .card__heading')) : [];
  }

  // Reset all title heights to auto for natural flow measurement
  function resetHeights(titles) {
    titles.forEach(title => title.style.height = 'auto');
  }

  // Get natural height of title element, handling offscreen elements
  function getNaturalHeight(title) {
    const isOffscreen = title.closest('.grid__item')?.classList.contains('scroll-trigger--offscreen');
    if (!isOffscreen) return title.offsetHeight;

    // Temporarily make offscreen element visible for measurement
    const card = title.closest('.grid__item'), originalClasses = card.className;
    card.classList.remove('scroll-trigger--offscreen');
    title.style.height = 'auto';
    title.offsetHeight; // Force reflow
    const height = title.offsetHeight;
    title.style.height = ''; // Restore
    card.className = originalClasses;
    return height;
  }

  // Align heights within a single row of titles
  function alignRow(titles) {
    if (!titles.length) return;

    resetHeights(titles);
    document.body.offsetHeight; // Force reflow

    const heights = titles.map(title => getNaturalHeight(title));
    const maxHeight = Math.max(...heights);

    if (maxHeight > 0) {
      titles.forEach(title => title.style.height = maxHeight + 'px');
    }
  }

  // Main alignment function - groups titles into rows and aligns them
  function alignTitles() {
    // Deactivate below breakpoint
    if (window.innerWidth < BREAKPOINT) {
      if (isInit) {
        resetHeights(getTitles());
        isInit = false;
      }
      return;
    }

    const titles = getTitles();
    if (!titles.length) return;

    // Group titles into rows
    const rows = [];
    for (let i = 0; i < titles.length; i += ITEMS_PER_ROW) {
      rows.push(titles.slice(i, i + ITEMS_PER_ROW));
    }

    rows.forEach(alignRow);
    isInit = true;
  }

  // Handle window resize events with debouncing
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(alignTitles, DEBOUNCE);
  }

  // Initialize the alignment system
  function init() {
    alignTitles(); // Run initial alignment

    // Add responsive event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => setTimeout(alignTitles, DEBOUNCE));

    // Monitor scroll animations for post-animation adjustments
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class' &&
            mutation.target.classList.contains('scroll-trigger') &&
            !mutation.target.classList.contains('scroll-trigger--offscreen')) {

          // Check if realignment is needed after animation completes
          setTimeout(() => {
            if (window.innerWidth >= BREAKPOINT && document.querySelector('#product-grid')) {
              const titles = getTitles();
              if (titles.length) {
                const rows = [];
                for (let i = 0; i < titles.length; i += ITEMS_PER_ROW) {
                  rows.push(titles.slice(i, i + ITEMS_PER_ROW));
                }

                let needsAlign = false;
                rows.forEach(row => {
                  if (row.length) {
                    resetHeights(row);
                    document.body.offsetHeight;
                    const heights = row.map(title => getNaturalHeight(title));
                    const maxHeight = Math.max(...heights);
                    if (maxHeight > 0) {
                      const currentHeights = row.map(title => parseFloat(title.style.height) || 0);
                      if (currentHeights.some(h => Math.abs(h - maxHeight) > 1)) {
                        needsAlign = true;
                      }
                    }
                  }
                });

                if (needsAlign) alignTitles();
              }
            }
          }, 200);
        }
      });
    });

    // Observe all scroll-trigger elements for animation completion
    document.querySelectorAll('.scroll-trigger').forEach(trigger =>
      observer.observe(trigger, { attributes: true, attributeFilter: ['class'] })
    );
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API for external control
  window.ProductCardAlignment = {
    destroy: function() {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', alignTitles);
      resetHeights(getTitles());
      isInit = false;
    },
    align: alignTitles
  };
})();